/**
 * Pusher Connection Recovery Service
 *
 * Handles automatic Pusher channel authentication recovery when auth fails.
 * Uses exponential backoff with jitter to retry channel subscriptions.
 */

import { NODE_ENV_LOCAL_OR_DEVELOPMENT } from 'Constants/env';
import { action, makeObservable, observable, runInAction, when } from 'mobx';
import type { RootStore } from 'Stores/RootStore';
import {
  retryWithBackoff,
  type BackoffOptions,
} from 'Utils/exponentialBackoff';
import { getLogger, Logger } from 'Utils/logger';

/**
 * Configuration options for the Pusher recovery service
 */
export interface PusherRecoveryOptions extends BackoffOptions {
  /** Whether to log recovery attempts to console */
  enableConsoleLogging?: boolean;
}

/**
 * Recovery attempt information
 */
export interface RecoveryAttempt {
  timestamp: number;
  attempt: number;
  error: string;
  nextRetryIn: number;
}

/**
 * Service for managing Pusher channel authentication recovery
 */
export class PusherConnectionRecoveryService {
  private rootStore: RootStore;
  private recoveryTimer?: NodeJS.Timer;
  private options: Required<PusherRecoveryOptions>;
  private logger: Logger;
  private lastRecoveryStart = 0;
  private readonly RECOVERY_COOLDOWN = 2000; // 2 second cooldown between recovery attempts
  private abortController: AbortController | null = null;

  @observable public isRecovering = false;
  @observable public lastRecoveryAttempt: RecoveryAttempt | null = null;
  @observable public recoveryAttemptCount = 0;

  constructor(rootStore: RootStore, options: PusherRecoveryOptions = {}) {
    this.rootStore = rootStore;
    this.options = {
      initialDelay: 1000, // Start with 1 second for Pusher auth retries
      maxDelay: 16000, // Cap at 16 seconds
      maxAttempts: 10, // Allow 10 attempts
      backoffFactor: 2, // 2x growth
      jitter: true,
      timeout: 20000,
      enableConsoleLogging: NODE_ENV_LOCAL_OR_DEVELOPMENT,
      ...options,
    };
    this.logger = getLogger();

    makeObservable(this);
  }

  /**
   * Initiates Pusher channel authentication recovery
   */
  @action
  public async startAuthRecovery(): Promise<void> {
    if (this.isRecovering) {
      this.consoleLog(
        'PusherConnectionRecoveryService already recovering, skipping'
      );
      return;
    }

    // Check cooldown period to prevent rapid successive recovery attempts
    const now = Date.now();
    if (now - this.lastRecoveryStart < this.RECOVERY_COOLDOWN) {
      this.consoleLog(
        `PusherConnectionRecoveryService recovery on cooldown, ${Math.ceil(
          (this.RECOVERY_COOLDOWN - (now - this.lastRecoveryStart)) / 1000
        )}s remaining`
      );
      return;
    }

    this.lastRecoveryStart = now;
    this.setIsRecovering(true);
    this.recoveryAttemptCount = 0;

    // Create new AbortController for this recovery attempt
    this.abortController = new AbortController();

    this.consoleLog('PusherConnectionRecoveryService starting auth recovery');
    this.logger.info('PusherConnectionRecoveryService starting auth recovery');

    try {
      await retryWithBackoff({
        ...this.options,
        operation: () => this.retryChannelAuth(),
        shouldRetry: (error: Error, attempt: number) =>
          this.shouldRetryAuth(error, attempt),
        onRetry: (error: Error, attempt: number, nextDelay: number) =>
          this.onRetryAttempt(error, attempt, nextDelay),
        abortSignal: this.abortController.signal,
      });

      // If we get here, auth recovery succeeded
      await this.onRecoverySuccess();
    } catch (error) {
      const err = error as Error;

      // Check if this was an abort rather than a failure
      if (err.message === 'Operation was aborted') {
        this.consoleLog('PusherConnectionRecoveryService recovery aborted');
        this.logger.info('PusherConnectionRecoveryService recovery aborted');

        // Clean up AbortController
        this.abortController = null;
        this.setIsRecovering(false);
      } else {
        // All retry attempts failed
        this.onRecoveryFailure(err);
      }
    }
  }

  /**
   * Stops any ongoing recovery attempts
   */
  @action
  public stopRecovery(): void {
    if (this.recoveryTimer) {
      clearTimeout(this.recoveryTimer);
      this.recoveryTimer = undefined;
    }

    // Abort any ongoing retry attempts
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    this.setIsRecovering(false);
    this.recoveryAttemptCount = 0;
    this.lastRecoveryAttempt = null;

    this.consoleLog('PusherConnectionRecoveryService recovery stopped');
  }

  private async retryChannelAuth(): Promise<void> {
    this.consoleLog(
      'PusherConnectionRecoveryService attempting channel auth retry'
    );

    const pusherStore = this.rootStore.pusherStore;
    const personStore = this.rootStore.personStore;

    try {
      const tokenRefreshSuccess =
        await personStore.refreshTokenIfNeededForPusher();

      if (!tokenRefreshSuccess) {
        const errorMsg =
          'Token refresh failed, cannot proceed with Pusher auth retry';
        this.logger.error(errorMsg);
        throw new Error(errorMsg);
      }
    } catch (error) {
      const errorMsg = `Token refresh error: ${(error as Error).message}`;
      this.logger.error(
        'PusherConnectionRecoveryService token refresh failed',
        { error }
      );
      throw new Error(errorMsg);
    }

    if (!pusherStore.isSocketConnected) {
      throw new Error('Socket not connected, skipping retry');
    }

    if (!navigator.onLine) {
      throw new Error('Browser is offline, skipping retry');
    }

    try {
      if (
        !pusherStore.isPersonalChannelActuallySubscribed ||
        !pusherStore.isAccountChannelActuallySubscribed
      ) {
        pusherStore.clearAllData();
        pusherStore.openConnection();

        try {
          await when(
            () =>
              pusherStore.isPersonalChannelActuallySubscribed &&
              pusherStore.isAccountChannelActuallySubscribed,
            { timeout: 1000 }
          );
        } catch (e) {
          // Timeout is expected, proceed to explicit check
        }
      }

      if (
        pusherStore.isPersonalChannelActuallySubscribed &&
        pusherStore.isAccountChannelActuallySubscribed
      ) {
        this.consoleLog(
          'PusherConnectionRecoveryService channels successfully subscribed'
        );
        return;
      }

      throw new Error('Channels failed to subscribe after retry');
    } catch (error) {
      const message = `Channel auth retry failed: ${(error as Error).message}`;
      this.consoleLog('PusherConnectionRecoveryService', message);
      throw error;
    }
  }

  /**
   * Determines if an auth error should trigger a retry
   */
  private shouldRetryAuth(error: Error, attempt: number): boolean {
    const pusherStore = this.rootStore.pusherStore;

    // Only retry if socket is connected, browser is online, and we haven't exceeded max attempts
    const shouldRetry =
      pusherStore.isSocketConnected &&
      navigator.onLine &&
      attempt < this.options.maxAttempts;

    this.consoleLog('PusherConnectionRecoveryService retry decision', {
      message: error.message,
      attempt,
      isSocketConnected: pusherStore.isSocketConnected,
      isOnline: navigator.onLine,
      shouldRetry,
    });

    return shouldRetry;
  }

  /**
   * Called on each retry attempt
   */
  private onRetryAttempt(
    error: Error,
    attempt: number,
    nextDelay: number
  ): void {
    runInAction(() => {
      this.recoveryAttemptCount = attempt + 1;
      this.lastRecoveryAttempt = {
        timestamp: Date.now(),
        attempt: attempt + 1,
        error: error.message,
        nextRetryIn: nextDelay,
      };
    });

    const retryDelaySeconds = Math.ceil(nextDelay / 1000);
    const message = `PusherConnectionRecoveryService auth retry attempt ${
      attempt + 1
    } failed, retrying in ${retryDelaySeconds} seconds. Error: ${
      error.message
    }`;

    this.consoleLog(message);
    this.logger.info(message);
  }

  /**
   * Called when recovery succeeds
   */
  private async onRecoverySuccess(): Promise<void> {
    this.consoleLog('PusherConnectionRecoveryService auth recovery succeeded');
    this.logger.info('PusherConnectionRecoveryService auth recovery succeeded');

    // Clean up AbortController
    this.abortController = null;

    this.setIsRecovering(false);
    this.recoveryAttemptCount = 0;
    this.lastRecoveryAttempt = null;

    this.consoleLog(
      'PusherConnectionRecoveryService channels are now subscribed'
    );
  }

  /**
   * Called when all recovery attempts fail
   */
  private onRecoveryFailure(error: Error): void {
    this.consoleLog(
      'PusherConnectionRecoveryService auth recovery failed after all attempts',
      error
    );
    this.logger.error(
      'PusherConnectionRecoveryService auth recovery failed after all attempts',
      error
    );

    // Clean up AbortController
    this.abortController = null;

    this.setIsRecovering(false);
  }

  @action
  private setIsRecovering(recovering: boolean): void {
    this.isRecovering = recovering;
  }

  /**
   * Conditional logging helper that only logs when enableConsoleLogging is true
   */
  private consoleLog(message: string, ...optionalParams: unknown[]): void {
    if (this.options.enableConsoleLogging) {
      console.info(message, ...optionalParams);
    }
  }
}
